/**
 * "Moji dizajni" — Zazzle's saved designs, for a shop without customer
 * accounts in the widget.
 *
 * The design itself is saved on the server as a share snapshot (the same one
 * "Podeli dizajn" makes: the answers and the addresses of their pictures), so
 * it does not expire with the one-week browser draft and opens on any device
 * from its link. This browser keeps the LIST — name, product, date, a small
 * thumbnail and the link — in localStorage, newest first. Cross-device listing
 * needs a signed-in customer identity from the shop and is a separate step.
 */

export const LIBRARY_STORAGE_KEY = 'pz-library';
export const LIBRARY_MAX_ENTRIES = 30;
/** A thumbnail data URL longer than this is dropped rather than stored. */
export const LIBRARY_MAX_THUMB = 40_000;

export interface LibraryEntry {
  /** The share snapshot's id. */
  id: string;
  name: string;
  /** The product page with the snapshot in it (`?pz_share=`). */
  url: string;
  productId: string;
  productName?: string;
  at: number;
  thumb?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseLibrary(raw: unknown): LibraryEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: LibraryEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const entry = item as Partial<LibraryEntry>;
    if (typeof entry.id !== 'string' || !UUID.test(entry.id)) continue;
    if (typeof entry.url !== 'string' || !/^https?:\/\//.test(entry.url)) continue;
    if (typeof entry.at !== 'number' || typeof entry.productId !== 'string') continue;
    out.push({
      id: entry.id,
      name: typeof entry.name === 'string' && entry.name.trim() ? entry.name.trim().slice(0, 80) : 'Dizajn',
      url: entry.url,
      productId: entry.productId,
      ...(typeof entry.productName === 'string' ? { productName: entry.productName.slice(0, 120) } : {}),
      at: entry.at,
      ...(typeof entry.thumb === 'string' && entry.thumb.startsWith('data:image/') && entry.thumb.length <= LIBRARY_MAX_THUMB
        ? { thumb: entry.thumb } : {}),
    });
  }
  return out.sort((a, b) => b.at - a.at).slice(0, LIBRARY_MAX_ENTRIES);
}

/** Newest first, one entry per snapshot, capped. */
export function addLibraryEntry(list: readonly LibraryEntry[], entry: LibraryEntry): LibraryEntry[] {
  return parseLibrary([entry, ...list.filter((item) => item.id !== entry.id)]);
}

export function removeLibraryEntry(list: readonly LibraryEntry[], id: string): LibraryEntry[] {
  return list.filter((item) => item.id !== id);
}

export function readLibrary(storage: Pick<Storage, 'getItem'> | undefined): LibraryEntry[] {
  try {
    return parseLibrary(JSON.parse(storage?.getItem(LIBRARY_STORAGE_KEY) ?? '[]'));
  } catch {
    return [];
  }
}

/**
 * Stores the list; when the browser's quota refuses it, the thumbnails go
 * first (they are a convenience, the links are the point).
 */
export function writeLibrary(storage: Pick<Storage, 'setItem'> | undefined, list: readonly LibraryEntry[]): boolean {
  if (!storage) return false;
  try {
    storage.setItem(LIBRARY_STORAGE_KEY, JSON.stringify(list));
    return true;
  } catch {
    try {
      storage.setItem(LIBRARY_STORAGE_KEY, JSON.stringify(list.map(({ thumb: _thumb, ...rest }) => rest)));
      return true;
    } catch {
      return false;
    }
  }
}
